import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from "@mui/material"
import React, { DragEvent, useState } from "react"
import { DirectType } from "../services/api/direct"
import { useAuthContext } from "../services/auth"
import { UserRole } from "../services/auth/auth.types"
import { useSnackbarContext } from "../services/snackbar"
import { httpClient } from "../services/http"
import { uploadPoster } from "../utils/upload"
import { FieldContainer, PageTitle, StyledTextField } from "../components/StyledElements"

export const AddDirect = (): React.ReactElement => {
  const [posterFile, setPosterFile] = useState<File | null>(null)
  const [title, setTitle] = useState<string>("")
  const [description, setDescription] = useState<string>("")
  const [date, setDate] = useState<string>(new Date().toISOString().split("T")[0])
  const [promotion, setPromotion] = useState<number | null>(null)
  const [privacy, setPrivacy] = useState<number>(1)
  const [gpaRestricted, setGpaRestricted] = useState<boolean>(false)
  const [liveUrl, setliveUrl] = useState<string>("")

  const [isUploading, setIsUploading] = useState<boolean>(false)

  const [isDraggingPoster, setIsDraggingPoster] = useState(false)

  const { role, gdaAccess } = useAuthContext()
  const { open: sbOpen } = useSnackbarContext()
  const theme = useTheme()

  const handlePosterFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
      setPosterFile(file || null)
    }
  }

  const handleDragOverPoster = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDraggingPoster(true)
  }

  const handleDragLeavePoster = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDraggingPoster(false)
  }

  const handleDropPoster = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDraggingPoster(false)
    const files = event.dataTransfer.files
    if (files.length > 0 && (files[0].type === "image/png" || files[0].type === "image/jpeg")) {
      setPosterFile(files[0])
    }
  }

  const handlePrivacyChange = (e: SelectChangeEvent<number>) => {
    setPrivacy(e.target.value as number)
  }

  const handleGpaRestrictedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGpaRestricted(e.target.checked)
  }

  const flushForm = () => {
    setPosterFile(null)
    setTitle("")
    setDescription("")
    setDate(new Date().toISOString().split("T")[0])
    setPromotion(null)
    setPrivacy(1)
    setliveUrl("")
  }

  const handleSubmit = async () => {
    setIsUploading(true)
    const data = {
      title: title,
      description: description,
      privacy: privacy,
      gpa_restricted: gpaRestricted,
      date: date.toString(),
      promotion: (promotion || -1).toString(),
      liveUrl: liveUrl,
    }

    let directResponse: DirectType | null = null

    try {
      directResponse = await httpClient.instance.post("directs", data, {
        headers: {
          "Content-Type": "application/json",
        },
      })
    } catch (error) {
      sbOpen("Erreur pendant la création du direct", "error")
    } finally {
      if (!directResponse) {
        sbOpen("Erreur pendant la création du direct", "error")
        setIsUploading(false)
      } else {
        try {
          directResponse && posterFile && (await uploadPoster(directResponse, posterFile, sbOpen))
          sbOpen("Direct créée avec succès", "success")
          flushForm()
        } catch {
          sbOpen("Erreur pendant l'upload", "error")
        } finally {
          setIsUploading(false)
        }
      }
    }
  }

  return (
    <>
      <PageTitle>{"Ajouter un direct"}</PageTitle>
      <Container maxWidth="xl">
        <Grid container direction="row">
          <Grid size={{ xs: 12, md: 6 }} container direction="row">
            <FieldContainer size={12}>
              <StyledTextField
                label="Titre"
                variant="standard"
                color="soft"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                focused
              />
            </FieldContainer>
            <FieldContainer size={12}>
              <StyledTextField
                label="Description"
                variant="outlined"
                color="soft"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                minRows={8}
                maxRows={12}
                focused
              />
            </FieldContainer>
            <FieldContainer size={12}>
              <StyledTextField
                label="Lien du live"
                variant="standard"
                color="soft"
                value={liveUrl}
                onChange={(e) => setliveUrl(e.target.value)}
                autoFocus
                focused
              />
            </FieldContainer>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} container direction="row">
            <FieldContainer size={12}>
              <FormControl fullWidth focused>
                <Input
                  id="poster-input"
                  type="file"
                  onChange={handlePosterFileInputChange}
                  sx={{ display: "none" }}
                  inputProps={{ style: { display: "none" } }}
                />
                <Box
                  sx={{
                    border: `2px solid ${theme.palette.soft.main}`,
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 5,
                    cursor: "pointer",
                    backgroundColor: isDraggingPoster ? "primary.dark" : "inherit",
                  }}
                  onClick={() => document.getElementById("poster-input")?.click()}
                  onDragOver={handleDragOverPoster}
                  onDragLeave={handleDragLeavePoster}
                  onDrop={handleDropPoster}
                >
                  {posterFile ? posterFile.name : "Choisir une affiche (.png ou .jpg)..."}
                </Box>
              </FormControl>
            </FieldContainer>
            <FieldContainer size={6}>
              <StyledTextField
                label="Date"
                variant="standard"
                color="soft"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                focused
              />
            </FieldContainer>
            <FieldContainer size={6}>
              <FormControl fullWidth variant="standard" focused>
                <StyledTextField
                  label="Promo"
                  type="text"
                  variant="standard"
                  color="soft"
                  value={promotion || ""}
                  onChange={(e) => setPromotion(e.target.value === "" ? null : parseInt(e.target.value))}
                  placeholder=""
                  slotProps={{
                    input: { startAdornment: <InputAdornment position="start">{"P"}</InputAdornment> },
                  }}
                />
              </FormControl>
            </FieldContainer>
            <FieldContainer size={4}>
              <Select
                value={privacy}
                variant="standard"
                color="soft"
                onChange={handlePrivacyChange}
                sx={{ minWidth: 120 }}
              >
                {role === UserRole.GEEK && <MenuItem value={0}>{"Publique"}</MenuItem>}
                <MenuItem value={1}>{"CS"}</MenuItem>
                <MenuItem value={2}>{"Hyris"}</MenuItem>
                {role === UserRole.GEEK && <MenuItem value={3}>{"Archivé"}</MenuItem>}
              </Select>
              <FormHelperText>{"Accès"}</FormHelperText>
            </FieldContainer>
            {gdaAccess && privacy === 2 && (
              <FieldContainer size={4}>
                <Checkbox checked={gpaRestricted} color="soft" onChange={handleGpaRestrictedChange} />
                <Typography variant="body1" component="span">
                  {"Caché aux GPA"}
                </Typography>
              </FieldContainer>
            )}
            <FieldContainer size={12}>
              <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={title === ""}>
                {posterFile ? "Valider & uploader" : "Valider"}
              </Button>
            </FieldContainer>
          </Grid>
        </Grid>
      </Container>

      <Backdrop sx={{ color: "#fff", zIndex: 300, display: "flex", flexDirection: "column" }} open={isUploading}>
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {"Upload en cours, veuillez ne pas quitter cette page..."}
        </Typography>
      </Backdrop>
    </>
  )
}
